import { useCallback, useEffect, useRef } from 'react';
import apiService from '@/services/apiService';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Analytics event types
export const ANALYTICS_EVENTS = {
  LANDING_VIEW: 'landing_view',
  VISA_SELECTION: 'visa_selection',
  DOCUMENT_REQUIREMENTS_VIEW: 'document_requirements_view',
  DOCUMENT_UPLOAD_STARTED: 'document_upload_started',
  DOCUMENT_UPLOAD_COMPLETED: 'document_upload_completed',
  PAYMENT_PAGE_VIEW: 'payment_page_view',
  PAYMENT_STARTED: 'payment_started',
  PAYMENT_COMPLETED: 'payment_completed',
  RESULTS_VIEW: 'results_view'
};

export const useAnalytics = (userId = null) => {
  const sessionId = useRef(getSessionId());

  const trackEvent = useCallback(async (eventType, metadata = null) => {
    try {
      await apiService.trackEvent(
        eventType,
        sessionId.current,
        userId,
        metadata
      );
    } catch (error) {
      // Silently fail - don't break user experience
      console.debug('Analytics tracking failed:', error);
    }
  }, [userId]);

  // Track page view on mount
  const trackPageView = useCallback((eventType, metadata = null) => {
    trackEvent(eventType, metadata);
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    sessionId: sessionId.current,
    EVENTS: ANALYTICS_EVENTS
  };
};

// Hook for tracking page views automatically
export const usePageViewTracking = (eventType, metadata = null, userId = null) => {
  const hasTracked = useRef(false);
  const { trackEvent } = useAnalytics(userId);

  useEffect(() => {
    if (!hasTracked.current) {
      trackEvent(eventType, metadata);
      hasTracked.current = true;
    }
  }, [eventType, metadata, trackEvent]);
};

export default useAnalytics;
